import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { IGeocodedAddress } from '../models/dms/address.model';
import { IGeolocation } from '../models/geolocation.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigBool } from '../sqlite/common/config';

interface ILocationState {
  // Whether or not the user has been asked to allow location services
  hasAskedPermision: boolean;
  allowPermission: boolean;
  lastPosition?: IGeolocation;
  address?: IGeocodedAddress;
}

const initialState: ILocationState = {
  hasAskedPermision: false,
  allowPermission: false,
  lastPosition: undefined,
  address: undefined,
};

/// THUNKS

export const setHasAskedPermissionStoreAsync = createAsyncThunk<{}, boolean>(
  'location/setHasAskedPermissionStoreAsync',
  async (val, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Setting has asked permission '${val}' to storage`, '[location] setHasAskedPermissionStoreAsync');
    try {
      dispatch(setHasAskedPermissionState(val));
      await updateConfigBool(ConfigName.LocationHasAskedPermission, val, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setAllowPermissionStoreAsync = createAsyncThunk<{}, boolean>(
  'location/setAllowPermissionStoreAsync',
  async (val, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Setting allow permission '${val}' to storage`, '[location] setAllowPermissionStoreAsync');
    try {
      dispatch(setAllowPermissionState(val));
      await updateConfigBool(ConfigName.LocationAllowPermission, val, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const resetDistanceTrackingDataStoreAsync = createAsyncThunk<{}, void>(
  'location/resetDistanceTrackingDataStoreAsync',
  async (nil, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Resetting distance tracking data`, '[location] resetDistanceTrackingDataStoreAsync');
    try {
      dispatch(setAddressState(undefined));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

/// SLICE

export const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setHasAskedPermissionState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[location] setHasAskedPermissionState: ${action.payload}`);
      state.hasAskedPermision = action.payload;
    },
    setAllowPermissionState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[location] setAllowPermissionState: ${action.payload}`);
      state.allowPermission = action.payload;
    },
    setLastPositionState: (state, action: PayloadAction<IGeolocation>) => {
      logConsole(LogType.Info, '[location] setLastPositionState');
      state.lastPosition = action.payload;
    },
    setAddressState: (state, action: PayloadAction<IGeocodedAddress | undefined>) => {
      logConsole(LogType.Info, '[location] setAddressState');
      state.address = action.payload;
    },
  }
});

/// REDUCERS

export const {
  setHasAskedPermissionState,
  setAllowPermissionState,
  setLastPositionState,
  setAddressState,
} = locationSlice.actions;

/// SELECTORS

export default locationSlice.reducer;
